import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@fan-env/environment';
import { ApiService } from '@fan-services/api.service';

declare global {
  interface Window {
    onTurnstileSuccess: (token: string) => void;
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      isExpired: (ref: string | HTMLElement) => boolean;
      reset: (ref: string | HTMLElement) => void;
      remove: (ref: string | HTMLElement) => void;
      execute: (ref: string | HTMLElement, options: any) => void;
    };
  }
}

@Component({
  selector: 'dash-captcha',
  template: ` <div #turnstileContainer class="captcha-container"></div> `,
  styleUrls: ['./captcha.component.scss'],
  imports: [CommonModule],
  providers: [ApiService],
})
export class CaptchaComponent implements OnDestroy {
  @ViewChild('turnstileContainer', { static: true }) turnstileContainer!: ElementRef;
  @Input() returnToken = false;
  @Output() onValidate = new EventEmitter<boolean>();
  @Output() onToken = new EventEmitter<string>();
  widgetId? = '';

  showComponent = false;
  turnstileKey = environment.turnstileKey;

  #apiService = inject(ApiService);
  #snackBar = inject(MatSnackBar);

  constructor(private zone: NgZone, private el: ElementRef) {}

  ngOnDestroy(): void {
    if (this.widgetId) {
      window.turnstile?.remove(this.widgetId);
    }
  }

  startCaptcha() {
    if (this.widgetId) {
      window.turnstile?.remove(this.widgetId);
    }
    this.widgetId = window.turnstile?.render(this.el.nativeElement.firstChild, {
      sitekey: this.turnstileKey,
      appearance: 'always',
      'refresh-expired': 'manual',
      retry: 'never',
      callback: (token: string) => {
        this.zone.run(() => {
          if (this.widgetId) {
            const expiredToken = window.turnstile?.isExpired(this.widgetId);
            if (token && expiredToken) {
              window.turnstile?.reset(this.turnstileContainer.nativeElement);
            } else {
              this.validateToken(token);
            }
          }
        });
      },
      'error-callback': (error: any) => {
        this.zone.run(() => {
          if (this.returnToken) {
            this.onToken.emit('');
            return;
          } else {
            this.onValidate.emit(false);
          }
        });
      },
    });
  }

  validateToken(token: string) {
    if (!token) {
      console.error('No token received');
      this.onValidate.emit(false);
      return;
    }
    if (this.returnToken) {
      this.onToken.emit(token);
      return;
    }
    this.#apiService.securityChecks.validateCaptcha(token).subscribe({
      next: (response) => {
        if (response.success) {
          this.onValidate.emit(true);
        } else {
          this.#snackBar.open('Error validating captcha');
          this.onValidate.emit(false);
        }
      },
      error: () => {
        this.#snackBar.open('Error validating captcha');
        this.onValidate.emit(false);
      },
    });
  }
}
